import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { sortByName } from '../../../utils/sort';
import { syncJunctionTable } from '../../../utils/syncJunctionTable';
import type { SettingsCategoryRow } from '../types';
import type { CategorySpecification, SpecificationKey } from '../../planning/types';

interface UseManageCategoriesReturn {
  categories: SettingsCategoryRow[];
  archivedCategories: SettingsCategoryRow[];
  archivedLoaded: boolean;
  loadArchived: () => Promise<void>;
  addCategory: (name: string, optionIds: string[], specifications: CategorySpecification[]) => Promise<boolean>;
  updateCategory: (id: string, name: string, optionIds: string[], specifications: CategorySpecification[]) => Promise<boolean>;
  archiveCategory: (id: string) => Promise<boolean>;
  unarchiveCategory: (id: string) => Promise<boolean>;
  deleteCategory: (id: string) => Promise<'ok' | 'has_machines' | 'error'>;
}

async function buildRows(data: SettingsCategoryRow[]): Promise<SettingsCategoryRow[]> {
  const ids = data.map((c) => c.id);
  if (!ids.length) return [];
  const [{ data: machineRows }, { data: templateRows }, { data: categoryOptionRows }, { data: categorySpecRows }] = await Promise.all([
    supabase.from('machines').select('category_id').in('category_id', ids),
    supabase.from('machine_templates').select('category_id').in('category_id', ids),
    supabase.from('category_options').select('category_id, option_id').in('category_id', ids),
    supabase.from('category_specifications').select('category_id, specification_key, required').in('category_id', ids),
  ]);
  const inUse = new Set([
    ...(machineRows ?? []).map((m) => m.category_id),
    ...(templateRows ?? []).map((t) => t.category_id),
  ]);
  const optionIdsByCategory: Record<string, string[]> = {};
  (categoryOptionRows ?? []).forEach((row: { category_id: string; option_id: string }) => {
    (optionIdsByCategory[row.category_id] ??= []).push(row.option_id);
  });
  const specsByCategory: Record<string, CategorySpecification[]> = {};
  (categorySpecRows ?? []).forEach((row: { category_id: string; specification_key: string; required: boolean }) => {
    (specsByCategory[row.category_id] ??= []).push({
      key: row.specification_key as SpecificationKey,
      required: row.required,
    });
  });
  return data.map((c) => ({
    ...c,
    has_machines: inUse.has(c.id),
    option_ids: optionIdsByCategory[c.id] ?? [],
    specifications: specsByCategory[c.id] ?? [],
  }));
}

export function useManageCategories(orgId: string | null): UseManageCategoriesReturn {
  const [categories, setCategories] = useState<SettingsCategoryRow[]>([]);
  const [archivedCategories, setArchivedCategories] = useState<SettingsCategoryRow[]>([]);
  const [archivedLoaded, setArchivedLoaded] = useState(false);

  const loadActive = async () => {
    if (!orgId) {
      setCategories([]);
      return;
    }
    try {
      const { data } = await supabase
        .from('categories')
        .select('*')
        .eq('organization_id', orgId)
        .eq('archived', false)
        .order('name');
      setCategories(await buildRows((data ?? []) as SettingsCategoryRow[]));
    } catch {
      setCategories([]);
    }
  };

  const refreshArchived = async (): Promise<boolean> => {
    if (!orgId) return false;
    try {
      const { data } = await supabase
        .from('categories')
        .select('*')
        .eq('organization_id', orgId)
        .eq('archived', true)
        .order('name');
      setArchivedCategories(await buildRows((data ?? []) as SettingsCategoryRow[]));
      return true;
    } catch {
      setArchivedCategories([]);
      return false;
    }
  };

  const loadArchived = async () => {
    if (!orgId || archivedLoaded) return;
    setArchivedLoaded(true);
    const ok = await refreshArchived();
    if (!ok) setArchivedLoaded(false);
  };

  useEffect(() => {
    setArchivedLoaded(false);
    setArchivedCategories([]);
    loadActive();
  }, [orgId]);

  const addCategory = async (
    name: string,
    optionIds: string[],
    specifications: CategorySpecification[],
  ): Promise<boolean> => {
    if (!orgId) return false;
    try {
      const { data, error } = await supabase
        .from('categories')
        .insert({
          name,
          organization_id: orgId,
          archived: false,
        })
        .select('id')
        .single();
      if (error) throw error;
      if (optionIds.length > 0 && data) {
        const { error: linkError } = await supabase.from('category_options').insert(
          optionIds.map((option_id) => ({ category_id: data.id, option_id, organization_id: orgId }))
        );
        if (linkError) throw linkError;
      }
      if (specifications.length > 0 && data) {
        const { error: specError } = await supabase.from('category_specifications').insert(
          specifications.map((s) => ({
            category_id: data.id,
            specification_key: s.key,
            required: s.required,
            organization_id: orgId,
          }))
        );
        if (specError) throw specError;
      }
      await loadActive();
      return true;
    } catch {
      return false;
    }
  };

  const updateCategory = async (
    id: string,
    name: string,
    optionIds: string[],
    specifications: CategorySpecification[],
  ): Promise<boolean> => {
    try {
      const { error } = await supabase.from('categories').update({ name }).eq('id', id);
      if (error) throw error;

      const previous = categories.find((c) => c.id === id) ?? archivedCategories.find((c) => c.id === id);
      await syncJunctionTable({
        table: 'category_options',
        fixedColumn: 'category_id',
        fixedValue: id,
        varyingColumn: 'option_id',
        previousIds: previous?.option_ids ?? [],
        nextIds: optionIds,
        organizationId: orgId,
      });

      // Specifications are always submitted as the full set from the modal (max 12 rows), so
      // delete+reinsert is simpler than diffing and there's no meaningful row-churn concern at
      // this scale — unlike option links, which can be large catalogs.
      const { error: delSpecErr } = await supabase.from('category_specifications').delete().eq('category_id', id);
      if (delSpecErr) throw delSpecErr;
      if (specifications.length > 0 && orgId) {
        const { error: insSpecErr } = await supabase.from('category_specifications').insert(
          specifications.map((s) => ({
            category_id: id,
            specification_key: s.key,
            required: s.required,
            organization_id: orgId,
          }))
        );
        if (insSpecErr) throw insSpecErr;
      }

      const update = (list: SettingsCategoryRow[]) =>
        sortByName(list.map((c) => (c.id === id ? { ...c, name, option_ids: optionIds, specifications } : c)));
      setCategories(update);
      setArchivedCategories(update);
      return true;
    } catch {
      return false;
    }
  };

  const archiveCategory = async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase.from('categories').update({ archived: true }).eq('id', id);
      if (error) throw error;
      setCategories((prev) => prev.filter((c) => c.id !== id));
      if (archivedLoaded) await refreshArchived();
      return true;
    } catch { return false; }
  };

  const unarchiveCategory = async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase.from('categories').update({ archived: false }).eq('id', id);
      if (error) throw error;
      setArchivedCategories((prev) => prev.filter((c) => c.id !== id));
      await loadActive();
      return true;
    } catch { return false; }
  };

  const deleteCategory = async (id: string): Promise<'ok' | 'has_machines' | 'error'> => {
    try {
      const { count: machinesCount } = await supabase
        .from('machines')
        .select('*', { count: 'exact', head: true })
        .eq('category_id', id);
      if ((machinesCount ?? 0) > 0) return 'has_machines';
      const { count: templatesCount } = await supabase
        .from('machine_templates')
        .select('*', { count: 'exact', head: true })
        .eq('category_id', id);
      if ((templatesCount ?? 0) > 0) return 'has_machines';
      const { error } = await supabase.from('categories').delete().eq('id', id);
      if (error) throw error;
      setCategories((prev) => prev.filter((c) => c.id !== id));
      setArchivedCategories((prev) => prev.filter((c) => c.id !== id));
      return 'ok';
    } catch {
      return 'error';
    }
  };

  return {
    categories,
    archivedCategories,
    archivedLoaded,
    loadArchived,
    addCategory,
    updateCategory,
    archiveCategory,
    unarchiveCategory,
    deleteCategory,
  };
}
